import React, { useEffect, useMemo, useRef, useState } from "react";
import { message } from "antd";
import axios from "axios";
import {
  Upload as UploadIcon,
  FileText,
  FileJson,
  CheckCircle,
  Circle,
  Download,
  Loader2,
  X,
  WandSparkles,
  ImageIcon,
} from "lucide-react";
import API_BASE from "../../config/api";
import { getActiveCustomerId } from "./imageCreationService";
import { ACTIVE_CUSTOMER_ID_CHANGED_EVENT } from "../../hooks/useMerchantStores";
import { useWorkflowState } from "./WorkflowStateContext";

// Menu Data Extraction — upload a menu photo or PDF, get back section/item
// JSON (title, price, description) via the existing /api/extract endpoint.
// This card only extracts and lets the merchant pick items; turning an item
// into an actual menu-card image happens in the separate Image Creation
// card. The two are connected by WorkflowSection, which passes selected
// items here → there via onSendToImageCreation / pendingItems, so neither
// card duplicates the other's UI.

// The server accepts up to 5MB per image. Small print is what gets lost, so an image is only shrunk when it has to
// be (to stay under this), never just to save bytes.
const MAX_EXTRACTION_UPLOAD_BYTES = 4.5 * 1024 * 1024;
const PDF_PAGE_TARGET_PX = 4000; // a PDF page is drawn so its longer side is about this many pixels (scale 2 to 3.5)
const ACCEPTED_TYPES = "image/*,application/pdf";

/**
 * Keeps an image at full detail and only shrinks it (quality first, then size) when it is over the server's size limit.
 * Non-image files and images already under the limit are returned untouched.
 */
const compressImageForExtraction = async (file, maxBytes = MAX_EXTRACTION_UPLOAD_BYTES) => {
  if (!file.type?.startsWith("image/") || file.size <= maxBytes) return file;
  const bitmap = await createImageBitmap(file);
  let scale = 1;
  let quality = 0.9;
  for (let attempt = 0; attempt < 8; attempt++) {
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
    if (blob && blob.size <= maxBytes) {
      bitmap.close?.();
      return new File([blob], "menu-page.jpg", { type: "image/jpeg", lastModified: Date.now() });
    }
    if (quality > 0.7) quality -= 0.1;
    else scale *= 0.8;
  }
  bitmap.close?.();
  throw new Error("This image is too large to upload. Try a smaller photo or a lower resolution.");
};

/** The page's text as [[string, x, y], ...] with x, y as 0-1 fractions of the page (top-left origin). */
const readPageText = async (page, natural) => {
  try {
    const content = await page.getTextContent();
    const out = [];
    for (const it of content.items || []) {
      const str = String(it.str || "").trim();
      if (!str || !it.transform) continue;
      const [vx, vy] = natural.convertToViewportPoint(it.transform[4], it.transform[5]); // handles page rotation / offsets
      const x = vx / natural.width;
      const y = vy / natural.height;
      if (x < -0.05 || x > 1.05 || y < -0.05 || y > 1.05) continue;
      out.push([str.slice(0, 200), +x.toFixed(4), +y.toFixed(4)]);
      if (out.length >= 8000) break;
    }
    return out;
  } catch {
    return [];
  }
};

/** Loads pdfjs-dist lazily (only when a PDF is actually processed) and
 * rasterizes every page to a JPEG File (with the page text). */
const rasterizePdfToImages = async (file) => {
  const pdfjsLib = await import("pdfjs-dist");
  pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const images = [];

  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
    const page = await pdf.getPage(pageNum);
    // Draw the page large: a dense flyer's small print is unreadable at the default size.
    const natural = page.getViewport({ scale: 1 });
    const scale = Math.min(3.5, Math.max(2, PDF_PAGE_TARGET_PX / Math.max(natural.width, natural.height)));
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: ctx, viewport }).promise;

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.9));
    // The text printed on the page (a digital PDF has it). The server uses it to spell names/prices exactly and to
    // notice items the picture alone would miss. Scanned PDFs have none, which is fine.
    const textItems = await readPageText(page, natural);
    if (blob) {
      images.push({ file: new File([blob], `menu-page-${pageNum}.jpg`, { type: "image/jpeg" }), textItems });
    }
    canvas.width = canvas.height = 0;
  }
  return images;
};

const extractFromImageFile = async (file, textItems = null) => {
  const compressed = await compressImageForExtraction(file);
  const formData = new FormData();
  formData.append("image", compressed);
  if (Array.isArray(textItems) && textItems.length) formData.append("textItems", JSON.stringify(textItems));
  // Lets the backend log this call's OpenAI token usage against the right
  // store, alongside Image Creation's usage — see AIExtractionUsage.
  const customerId = getActiveCustomerId();
  if (customerId) formData.append("customerId", customerId);
  const response = await axios.post(`${API_BASE}/extract`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  const sections = response?.data?.sections;
  if (!Array.isArray(sections)) throw new Error("Invalid extraction response.");
  return sections;
};

const downloadJson = (data, filename) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const mergeNorm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

const mergePriceKey = (p) =>
  String(p ?? "")
    .replace(/\$/g, "")
    .split("/")
    .map((x) => parseFloat(x.replace(/[^0-9.]/g, "")))
    .filter((n) => Number.isFinite(n) && n > 0)
    .map((n) => n.toFixed(2))
    .join("/");

// Drops repeated items (same name and price, or a repeat that has no price) across all pages and sections; the first copy stays.
const dropDuplicateItems = (sections) => {
  const seen = new Map();
  sections.forEach((sec) => {
    sec.items = (sec.items || []).filter((item) => {
      const key = mergeNorm(item.name);
      const list = seen.get(key) || [];
      const pk = mergePriceKey(item.price);
      const dup = list.find((x) => {
        const xp = mergePriceKey(x.price);
        return !xp || !pk || xp === pk;
      });
      if (dup) {
        if (!mergePriceKey(dup.price) && pk) {
          dup.price = item.price;
          if (item.variations) dup.variations = item.variations;
        }
        if (!String(dup.description || "").trim() && String(item.description || "").trim()) dup.description = item.description;
        return false;
      }
      list.push(item);
      seen.set(key, list);
      return true;
    });
  });
  return sections.filter((s) => s.items.length);
};

/** Pages of one menu often repeat a heading ("Beverages" on pages 1 and 3): join sections that have the same name. */
const mergeSectionsByName = (sections) => {
  const out = [];
  sections.forEach((section) => {
    const existing = out.find((s) => mergeNorm(s.name) === mergeNorm(section.name));
    if (!existing) {
      out.push({ ...section, items: [...(section.items || [])] });
      return;
    }
    if (!existing.variationColumnHeader && section.variationColumnHeader) existing.variationColumnHeader = section.variationColumnHeader;
    existing.items.push(...(section.items || []));
  });
  return dropDuplicateItems(out);
};

export default function MenuDataExtraction({ onRemove, onSendToImageCreation, hasImageCreationCard, onSendToImageExtraction, hasImageExtractionCard }) {
  const fileInputRef = useRef(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState("");
  const [extractError, setExtractError] = useState("");

  // Extracted JSON + selection live above the router (WorkflowStateProvider in
  // App.js), so they're still here when the merchant leaves this page and
  // comes back — only one item can be sent at a time.
  const {
    extractedSections,
    setExtractedSections,
    extractionSelectedKey: selectedKey,
    setExtractionSelectedKey: setSelectedKey,
  } = useWorkflowState();

  // A different store's extracted menu shouldn't linger when switching stores
  // without leaving the page (mirrors ImageCreation's same reset).
  useEffect(() => {
    const handleStoreChanged = () => {
      setExtractedSections([]);
      setSelectedKey(null);
    };
    window.addEventListener(ACTIVE_CUSTOMER_ID_CHANGED_EVENT, handleStoreChanged);
    return () => window.removeEventListener(ACTIVE_CUSTOMER_ID_CHANGED_EVENT, handleStoreChanged);
  }, [setExtractedSections, setSelectedKey]);

  const flatItems = useMemo(() => {
    const out = [];
    extractedSections.forEach((section, sIdx) => {
      (section.items || []).forEach((item, iIdx) => {
        out.push({ key: `${sIdx}:${iIdx}`, sectionName: section.name || "Menu", item });
      });
    });
    return out;
  }, [extractedSections]);

  const totalItemCount = flatItems.length;
  const hasSelection = selectedKey != null;

  const resetExtraction = () => {
    setExtractedSections([]);
    setSelectedKey(null);
    setExtractError("");
  };

  const handlePickFile = () => fileInputRef.current?.click();

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const isImage = file.type.startsWith("image/");
    const isPdf = file.type === "application/pdf";
    if (!isImage && !isPdf) {
      message.error("Please choose an image (PNG/JPG) or a PDF menu.");
      return;
    }
    setSelectedFile(file);
    resetExtraction();
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    resetExtraction();
  };

  const handleProcess = async () => {
    if (!selectedFile) {
      message.warning("Choose an image or PDF menu first.");
      return;
    }
    setIsProcessing(true);
    setExtractError("");
    try {
      let pageFiles;
      if (selectedFile.type === "application/pdf") {
        setProcessingStatus("Reading PDF pages...");
        pageFiles = await rasterizePdfToImages(selectedFile);
        if (!pageFiles.length) throw new Error("Couldn't read any pages from that PDF.");
      } else {
        pageFiles = [{ file: selectedFile, textItems: null }];
      }

      const pageSections = [];
      const failedPages = [];
      let lastError = null;
      for (let i = 0; i < pageFiles.length; i++) {
        setProcessingStatus(
          pageFiles.length > 1 ? `Extracting page ${i + 1} of ${pageFiles.length}...` : "Extracting menu data...",
        );
        // One page failing must not throw away the pages that worked.
        try {
          pageSections.push(...(await extractFromImageFile(pageFiles[i].file, pageFiles[i].textItems)));
        } catch (pageError) {
          console.error(`Menu extraction failed on page ${i + 1}:`, pageError);
          failedPages.push(i + 1);
          lastError = pageError;
        }
      }
      if (failedPages.length === pageFiles.length) throw lastError || new Error("Failed to extract menu data.");

      const allSections = mergeSectionsByName(pageSections);
      setExtractedSections(allSections);
      setSelectedKey(null);
      const itemCount = allSections.reduce((n, s) => n + (s.items?.length || 0), 0);
      if (failedPages.length) {
        message.warning(`Extracted ${itemCount} item${itemCount === 1 ? "" : "s"}, but page${failedPages.length === 1 ? "" : "s"} ${failedPages.join(", ")} could not be read. Run it again to retry.`);
      } else {
        message.success(`Extracted ${itemCount} item${itemCount === 1 ? "" : "s"}.`);
      }
    } catch (error) {
      console.error("Menu extraction error:", error);
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error.message ||
        "Failed to extract menu data.";
      setExtractError(msg);
      message.error(msg);
    } finally {
      setIsProcessing(false);
      setProcessingStatus("");
    }
  };

  // Single-select: picking an item deselects any other; clicking it again clears it.
  const selectItem = (key) => {
    setSelectedKey((prev) => (prev === key ? null : key));
  };

  const handleDownloadJson = () => {
    if (!extractedSections.length) return;
    downloadJson({ sections: extractedSections }, `menu-extraction-${Date.now()}.json`);
  };

  // Shares the selected item's name with the Image Extraction card, which looks for its picture in
  // that card's own PDF and in this menu when the menu is a PDF (an image menu can't be searched there).
  const handleSendToImageExtraction = () => {
    const selected = flatItems.find((f) => f.key === selectedKey);
    if (!selected) return;
    const pdf = selectedFile?.type === "application/pdf" ? selectedFile : null;
    onSendToImageExtraction?.({
      name: String(selected.item.name || "").trim(),
      price: selected.item.price ?? "",
      description: String(selected.item.description || "").trim(),
      file: pdf,
    });
    message.success(`Sent "${selected.item.name || "item"}" to Image Extraction.`);
  };

  const handleSendToImageCreation = () => {
    const selected = flatItems.find((f) => f.key === selectedKey);
    if (!selected) return;
    onSendToImageCreation?.([selected.item]);
    setSelectedKey(null);
    message.success(`Sent "${selected.item.name || "item"}" to Image Creation.`);
  };

  const disabled = isProcessing;

  return (
    <article className="rounded-2xl border border-gray-200 bg-white shadow-sm">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3 sm:px-5">
        <div className="flex items-center gap-3">
          <div className="grid h-9 w-9 place-items-center rounded-lg bg-indigo-50 text-indigo-600">
            <FileJson size={18} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 sm:text-base">Menu Data Extraction</h3>
            <p className="text-xs text-gray-500">Upload a menu photo or PDF and get item title, price and description as JSON</p>
          </div>
        </div>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            disabled={isProcessing}
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:border-gray-400 disabled:opacity-50"
          >
            Remove from Components
          </button>
        )}
      </header>

      <div className="flex flex-col gap-5 p-4 sm:p-5">
        {/* ---------- Upload ---------- */}
        <div className="flex flex-col gap-2">
          {selectedFile ? (
            <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 p-2.5">
              <div className="grid h-10 w-10 flex-none place-items-center rounded-lg bg-white text-indigo-600">
                <FileText size={18} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-gray-900">{selectedFile.name}</p>
                <p className="text-xs text-gray-500">{(selectedFile.size / (1024 * 1024)).toFixed(2)} MB</p>
              </div>
              <button
                type="button"
                onClick={handleClearFile}
                disabled={disabled}
                aria-label="Remove file"
                className="flex-none rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-red-600 disabled:opacity-50"
              >
                <X size={15} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={handlePickFile}
              className="flex flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center transition-colors hover:border-indigo-400"
            >
              <UploadIcon size={22} className="text-indigo-600" />
              <span className="text-sm font-medium text-gray-900">Click to upload a menu image or PDF</span>
              <span className="text-xs text-gray-500">PNG, JPG or PDF</span>
            </button>
          )}
          <input ref={fileInputRef} type="file" accept={ACCEPTED_TYPES} hidden onChange={handleFileChange} />

          <button
            type="button"
            onClick={handleProcess}
            disabled={!selectedFile || isProcessing}
            className="inline-flex items-center justify-center gap-2 self-start rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isProcessing && <Loader2 size={15} className="animate-spin" />}
            {isProcessing ? processingStatus || "Extracting..." : "Extract Menu Data"}
          </button>
          {extractError && <p className="text-xs text-red-600">{extractError}</p>}
        </div>

        {/* ---------- Extracted items ---------- */}
        {totalItemCount > 0 && (
          <div className="flex flex-col gap-3 border-t border-gray-200 pt-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm font-medium text-gray-900">
                {totalItemCount} item{totalItemCount === 1 ? "" : "s"} extracted{hasSelection ? " · 1 selected" : ""}
              </span>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={handleDownloadJson}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  <Download size={13} /> Download JSON
                </button>
                <button
                  type="button"
                  onClick={resetExtraction}
                  aria-label="Remove the extracted menu data"
                  title="Remove the extracted menu data"
                  className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                >
                  <X size={15} />
                </button>
              </div>
            </div>

            <div className="flex max-h-80 flex-col gap-1.5 overflow-y-auto rounded-xl border border-gray-200 p-2">
              {flatItems.map(({ key, sectionName, item }, idx) => {
                const isSelected = selectedKey === key;
                const showSectionHeader = idx === 0 || flatItems[idx - 1].sectionName !== sectionName;
                return (
                  <React.Fragment key={key}>
                    {showSectionHeader && (
                      <div className="mt-1.5 px-1 text-xs font-semibold uppercase tracking-wide text-gray-400 first:mt-0">
                        {sectionName}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => selectItem(key)}
                      className={`flex items-start gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors ${
                        isSelected ? "bg-indigo-50" : "hover:bg-gray-50"
                      }`}
                    >
                      {isSelected ? (
                        <CheckCircle size={16} className="mt-0.5 flex-none text-indigo-600" />
                      ) : (
                        <Circle size={16} className="mt-0.5 flex-none text-gray-300" />
                      )}
                      <span className="min-w-0 flex-1">
                        <span className="flex items-baseline justify-between gap-2">
                          <span className="truncate text-sm font-medium text-gray-900">{item.name || "Untitled item"}</span>
                          {item.price != null && item.price !== "" && (
                            <span className="flex-none text-sm font-semibold text-gray-700">{item.price}</span>
                          )}
                        </span>
                        {item.description && <span className="block truncate text-xs text-gray-500">{item.description}</span>}
                      </span>
                    </button>
                  </React.Fragment>
                );
              })}
            </div>

            <div className="flex flex-col gap-1.5 border-t border-gray-200 pt-3">
              <button
                type="button"
                onClick={handleSendToImageCreation}
                disabled={!hasSelection}
                className="inline-flex items-center justify-center gap-2 self-start rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <WandSparkles size={15} />
                Send to Image Creation
              </button>
              <button
                type="button"
                onClick={handleSendToImageExtraction}
                disabled={!hasSelection || !hasImageExtractionCard}
                title={hasImageExtractionCard ? "" : "Add the Image Extraction component to your Components first"}
                className="inline-flex items-center justify-center gap-2 self-start rounded-lg border border-indigo-200 px-4 py-2 text-sm font-semibold text-indigo-600 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ImageIcon size={15} />
                Send to Image Extraction
              </button>
              {!hasImageExtractionCard && (
                <p className="text-xs text-gray-500">Add the Image Extraction card from the Library to find this item's picture in a PDF.</p>
              )}
              <p className="text-xs text-gray-500">
                {hasImageCreationCard
                  ? "Select one item, then send it to the Image Creation card, where you'll choose whether to keep the reference image or generate a new one before it creates the menu card."
                  : "Add the Image Creation card from the Library to generate menu cards from a selected item."}
              </p>
            </div>
          </div>
        )}
      </div>
    </article>
  );
}
